import { calculateWorkOrderCosts } from '@calculations/cost/work-order';
import {
  calculateGrossMarginFromUnitPrice,
  calculateUnitPriceFromGrossMargin,
} from '@calculations/price/utilities/gross-margin';

import { storeToCalculationOperations } from '../../transforms/store-to-calculation-operations';
import { transformWorkOrderCostsToStoreState } from '../../utilities/transform-work-order-costs';
import {
  OnQuantityChangeInput,
  OnQuantityChangeOutput,
  OnQuotePriceCreationInput,
  OnQuotePriceCreationOutput,
  OnUnitPriceChangeInput,
  OnUnitPriceChangeOutput,
  RecalculatePriceStrategy,
} from '../types';
import {
  OnBaseGrossMarginChangeInput,
  OnBaseGrossMarginChangeOutput,
} from '../types/base-gross-margin';

function onQuantityChange({
  state,
  quotePrice,
  quoteLine,
  quantity,
}: OnQuantityChangeInput): OnQuantityChangeOutput {
  if (!quoteLine.snapWorkOrder) {
    return {
      status: 'failure',
      reason: 'No Snap Work Order Found',
    };
  }

  if (!quotePrice.baseGrossMargin) {
    return {
      status: 'failure',
      reason: 'No Base Gross Margin Found',
    };
  }

  if (!quoteLine.priceAdjustment) {
    return {
      status: 'failure',
      reason: 'No Price Adjustment Found',
    };
  }

  const costs = calculateWorkOrderCosts(
    {
      quantity,
      snapOperations: storeToCalculationOperations(quoteLine.snapWorkOrder),
    },
    state.calculationConfig,
  );

  // Calculate unit price based on target gross margin
  const baseUnitPrice = calculateUnitPriceFromGrossMargin(
    {
      unitCost: costs.unitCost,
      grossMargin: quotePrice.baseGrossMargin,
    },
    state.calculationConfig,
  );

  // Calculate price adjustment amount from base unit price
  const priceAdjustmentAmount = baseUnitPrice.times(quoteLine.priceAdjustment);

  // Calculate unit price based on price adjustment amount
  const unitPrice = baseUnitPrice.plus(priceAdjustmentAmount);

  // Calculate gross margin from unit price
  const grossMargin = calculateGrossMarginFromUnitPrice(
    {
      unitCost: costs.unitCost,
      unitPrice,
    },
    state.calculationConfig,
  );

  return {
    status: 'success',
    unitPrice,
    baseUnitPrice,
    priceAdjustmentAmount,
    grossMargin,
    ...transformWorkOrderCostsToStoreState(costs),
  };
}

function onBaseGrossMarginChange({
  state,
  quotePrice,
  quoteLine,
  baseGrossMargin,
}: OnBaseGrossMarginChangeInput): OnBaseGrossMarginChangeOutput {
  if (!quotePrice.unitCost) {
    return {
      status: 'failure',
      reason: 'No unit cost found',
    };
  }

  if (!quoteLine.priceAdjustment) {
    return {
      status: 'failure',
      reason: 'No Price Adjustment Found',
    };
  }

  // Calculate unit price based on target gross margin
  const baseUnitPrice = calculateUnitPriceFromGrossMargin(
    {
      unitCost: quotePrice.unitCost,
      grossMargin: baseGrossMargin,
    },
    state.calculationConfig,
  );

  // Calculate price adjustment amount from base unit price
  const priceAdjustmentAmount = baseUnitPrice.times(quoteLine.priceAdjustment);

  // Calculate unit price based on price adjustment amount
  const unitPrice = baseUnitPrice.plus(priceAdjustmentAmount);

  // Calculate gross margin from unit price
  const grossMargin = calculateGrossMarginFromUnitPrice(
    {
      unitCost: quotePrice.unitCost,
      unitPrice,
    },
    state.calculationConfig,
  );

  return {
    status: 'success',
    unitPrice,
    baseUnitPrice,
    priceAdjustmentAmount,
    grossMargin,
  };
}

function onUnitPriceChange({
  state,
  quotePrice,
  unitPrice,
}: OnUnitPriceChangeInput): OnUnitPriceChangeOutput {
  if (!quotePrice.unitCost) {
    return {
      status: 'failure',
      reason: 'No unit cost found',
    };
  }

  const grossMargin = calculateGrossMarginFromUnitPrice(
    {
      unitCost: quotePrice.unitCost,
      unitPrice,
    },
    state.calculationConfig,
  );

  return {
    status: 'success',
    grossMargin,
  };
}

function onQuotePriceCreation({
  state,
  quoteLine,
  quotePrice,
}: OnQuotePriceCreationInput): OnQuotePriceCreationOutput {
  const { quantity } = quotePrice;

  if (!quoteLine.snapWorkOrder) {
    return {
      status: 'failure',
      reason: 'No Snap Work Order Found',
    };
  }

  if (!quotePrice.baseGrossMargin) {
    return {
      status: 'failure',
      reason: 'No Base Gross Margin Found',
    };
  }

  if (!quoteLine.priceAdjustment) {
    return {
      status: 'failure',
      reason: 'No Price Adjustment Found',
    };
  }

  const costs = calculateWorkOrderCosts(
    {
      quantity,
      snapOperations: storeToCalculationOperations(quoteLine.snapWorkOrder),
    },
    state.calculationConfig,
  );

  // Calculate unit price based on target gross margin
  const baseUnitPrice = calculateUnitPriceFromGrossMargin(
    {
      unitCost: costs.unitCost,
      grossMargin: quotePrice.baseGrossMargin,
    },
    state.calculationConfig,
  );

  // Calculate price adjustment amount from base unit price
  const priceAdjustmentAmount = baseUnitPrice.times(quoteLine.priceAdjustment);

  // Calculate unit price based on price adjustment amount
  const unitPrice = baseUnitPrice.plus(priceAdjustmentAmount);

  // Calculate gross margin from unit price
  const grossMargin = calculateGrossMarginFromUnitPrice(
    {
      unitCost: costs.unitCost,
      unitPrice,
    },
    state.calculationConfig,
  );

  return {
    status: 'success',
    quotePrice: {
      ...quotePrice,
      unitPrice,
      grossMargin,
      ...transformWorkOrderCostsToStoreState(costs),
    },
  };
}

export const recalculateCAPPriceSegmentation = {
  onQuantityChange,
  onBaseGrossMarginChange,
  onUnitPriceChange,
  onContributionMarginChange: null,
  onGrossMarginChange: null,
  onQuotePriceCreation,
  onUnitCostChange: null,
} satisfies RecalculatePriceStrategy;
